import { relations, sql } from 'drizzle-orm'
import {
  check,
  index,
  pgEnum,
  pgTable,
  text,
  uniqueIndex,
} from 'drizzle-orm/pg-core'
import {
  getBaseFields,
  getBaseIndexes,
  getIdReference,
  getTimestampWithTimezone,
} from '../common/utils'
import { customers } from './customer'
import { users } from './user'

// Two orthogonal axes on a membership row.
//
// `accessRole` — pure RBAC: the level of access this membership grants. Every
// value here grants access; absence (null) means the row records a relationship
// but no app access. securityFilters treats `accessRole IS NOT NULL` as the
// access gate — keep this invariant when adding values to the enum.
//
// `relationshipType` — pure CRM: what kind of relationship this person has to
// the customer. Never grants access on its own. Later product RBAC scoping
// (resident vs board vs property_manager surfaces) will read this column.
//
// A CRM-recorded contact (e.g. the asoy prospecting import, a sales-recorded
// isännöitsijä) lives as accessRole=null + relationshipType=<kind>. A real
// onboarded user has accessRole set, and optionally a relationshipType
// describing who they are to the customer.
export const accessRoleEnum = pgEnum('access_role', ['owner', 'member'])

export const relationshipTypeEnum = pgEnum('relationship_type', [
  'property_manager',
  'board_chair',
  'board',
  'other',
])

export const customerMembers = pgTable(
  'customer_members',
  {
    ...getBaseFields(),
    customerId: getIdReference(() => customers.id),
    userId: getIdReference(() => users.id),
    accessRole: accessRoleEnum(),
    relationshipType: relationshipTypeEnum(),
    // Edge provenance: which import created this membership row. Nullable —
    // existing rows and other writers (asoy_prospecting_db, manual, onboarding)
    // leave it null. Stamped 'profinder' by the Profinder import. NOT a marketing
    // touchpoint, so it deliberately does not reuse internalFirstTouchAcquisition.
    source: text(),
    joinedAt: getTimestampWithTimezone().notNull().defaultNow(),
  },
  (table) => [
    ...getBaseIndexes('customer_members', table),
    index('customer_members_customer_id_idx').on(table.customerId),
    index('customer_members_user_id_idx').on(table.userId),
    // A membership row must mean something — either it grants access, or it
    // records a relationship, or both. An empty row is a bug.
    check(
      'customer_members_has_meaning',
      sql`${table.accessRole} IS NOT NULL OR ${table.relationshipType} IS NOT NULL`,
    ),
    // A person has at most one live membership per customer (accessRole +
    // relationshipType live on the same row). The existing check-before-insert
    // guards already maintained this invariant — this index enforces it at the
    // DB level and enables onConflict upserts. Soft-deleted rows are excluded
    // so a re-import after deletion creates a new live row correctly.
    uniqueIndex('customer_members_customer_user_unique_idx')
      .on(table.customerId, table.userId)
      .where(sql`${table.deletedAt} IS NULL`),
  ],
)

export const customerMembersRelations = relations(
  customerMembers,
  ({ one }) => ({
    customer: one(customers, {
      fields: [customerMembers.customerId],
      references: [customers.id],
    }),
    user: one(users, {
      fields: [customerMembers.userId],
      references: [users.id],
    }),
  }),
)
